import { Box, Button, ButtonGroup, Chip, MenuItem, TextField } from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useMemo, useState } from "react";
import { CategoryPickerPanel } from "../../components/CategoryPickerPanel";
import { DatePickerField } from "../../components/DatePickerField";
import { AccountSelect } from "../../components/FinanceSelects";
import { MoneyInputGroup, recordFieldLabel } from "../../components/MoneyInputGroup";
import { PanelHeader } from "../../components/Panel";
import { SidePanel } from "../../components/SidePanel";
import { TagPickerPanel } from "../../components/TagPickerPanel";
import { financeApi } from "../../lib/api";
import { appToday } from "../../lib/devDate";
import { CategorySelectOption, dateInputValue } from "../../lib/format";
import { recurrenceFrequencyUnits } from "../../lib/labels";
import { queryKeys } from "../../lib/queryKeys";
import type {
  Account,
  Category,
  Currency,
  Group,
  Member,
  RecurrenceFrequency,
  RecurringPayment,
  RecurringPaymentPayload,
  Tag,
  TransactionType,
} from "../../lib/types";

type EditorErrors = {
  name?: string;
  amount?: string;
  account_id?: string;
};

export function RecurringPaymentEditor({
  group,
  payment,
  accounts,
  categories,
  tags,
  members,
  currencies,
  onClose,
}: {
  group: Group;
  payment: RecurringPayment | null;
  accounts: Account[];
  categories: Category[];
  tags: Tag[];
  members: Member[];
  currencies: Currency[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<TransactionType>(payment?.type || "expense");
  const [name, setName] = useState(payment?.name || "");
  const [amount, setAmount] = useState(payment?.amount || "");
  const [currency, setCurrency] = useState(payment?.currency || group.base_currency || currencies[0]?.code || "EUR");
  const [accountId, setAccountId] = useState(payment?.account_id || "");
  const [categoryId, setCategoryId] = useState(payment?.category_id || "");
  const [tagIds, setTagIds] = useState<string[]>(payment?.tag_ids || []);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [frequency, setFrequency] = useState<RecurrenceFrequency>(payment?.frequency || "monthly");
  const [interval, setInterval] = useState(String(payment?.interval || 1));
  const [startDate, setStartDate] = useState(payment?.start_date || dateInputValue(appToday()));
  const [endDate, setEndDate] = useState(payment?.end_date || "");
  const [note, setNote] = useState(payment?.note || "");
  const [errors, setErrors] = useState<EditorErrors>({});
  const [status, setStatus] = useState("");

  const activeAccounts = accounts.filter((account) => !account.is_archived || account.id === payment?.account_id);
  const selectedCategory = categories.find((category) => category.id === categoryId);
  const selectedTags = useMemo(
    () => tagIds.map((tagId) => tags.find((tag) => tag.id === tagId)).filter((tag): tag is Tag => Boolean(tag)),
    [tagIds, tags],
  );

  const saveMutation = useMutation({
    mutationFn: (payload: RecurringPaymentPayload) =>
      payment
        ? financeApi.updateRecurringPayment(group.id, payment.id, payload)
        : financeApi.createRecurringPayment(group.id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.recurringPayments(group.id) });
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: EditorErrors = {};
    const trimmedName = name.trim();
    const amountNumber = Number(amount.replace(",", "."));
    if (!trimmedName) nextErrors.name = "Enter a payment name.";
    if (!amount.trim() || !Number.isFinite(amountNumber) || amountNumber <= 0) nextErrors.amount = "Enter an amount greater than zero.";
    if (!accountId) nextErrors.account_id = "Select an account.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const intervalNumber = Math.min(36, Math.max(1, Math.round(Number(interval) || 1)));
    saveMutation.mutate({
      account_id: accountId,
      category_id: categoryId || undefined,
      type,
      name: trimmedName,
      amount: amount.replace(",", "."),
      currency,
      frequency,
      interval: intervalNumber,
      start_date: startDate,
      end_date: endDate || undefined,
      tag_ids: tagIds,
      note: note.trim() || undefined,
    });
  };

  return (
    <SidePanel ariaLabelledBy="recurringPaymentEditorTitle" className="record-modal" onClose={onClose}>
      {({ close }) => (
        <>
          <PanelHeader
            titleId="recurringPaymentEditorTitle"
            title={payment ? "Edit recurring payment" : "Add recurring payment"}
            closeAriaLabel="Close recurring payment dialog"
            onClose={close}
          />
          <form className="records-form" noValidate onSubmit={submit}>
            <Box className="records-type-toggle">
              <ButtonGroup variant="outlined" aria-label="Payment type">
                <Button
                  type="button"
                  variant={type === "expense" ? "contained" : "outlined"}
                  onClick={() => {
                    setType("expense");
                    setCategoryId("");
                  }}
                >
                  Expense
                </Button>
                <Button
                  type="button"
                  variant={type === "income" ? "contained" : "outlined"}
                  onClick={() => {
                    setType("income");
                    setCategoryId("");
                  }}
                >
                  Income
                </Button>
              </ButtonGroup>
            </Box>
            <Box className="records-field">
              <TextField
                required
                label={recordFieldLabel("Name", <LabelOutlinedIcon fontSize="small" aria-hidden="true" />)}
                name="name"
                placeholder="Netflix"
                value={name}
                error={Boolean(errors.name)}
                helperText={errors.name}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 120 } }}
                onChange={(event) => {
                  setName(event.target.value);
                  setErrors((current) => ({ ...current, name: undefined }));
                }}
              />
            </Box>
            <Box className="records-field">
              <MoneyInputGroup
                label="Amount"
                name="amount"
                amount={amount}
                currency={currency}
                currencies={currencies}
                error={Boolean(errors.amount)}
                helperText={errors.amount}
                labelIcon={<PaidOutlinedIcon fontSize="small" aria-hidden="true" />}
                onAmountChange={(next) => {
                  setAmount(next);
                  setErrors((current) => ({ ...current, amount: undefined }));
                }}
                onCurrencyChange={setCurrency}
              />
            </Box>
            <Box className="records-field">
              <AccountSelect
                label={recordFieldLabel("Account", <AccountBalanceOutlinedIcon fontSize="small" aria-hidden="true" />)}
                name="account_id"
                required
                accounts={activeAccounts}
                members={members}
                emptyLabel="Select account"
                error={Boolean(errors.account_id)}
                helperText={errors.account_id}
                value={accountId}
                onChange={(nextAccountId) => {
                  const nextAccount = accounts.find((account) => account.id === nextAccountId);
                  setAccountId(nextAccountId);
                  if (nextAccount?.currency) setCurrency(nextAccount.currency);
                  setErrors((current) => ({ ...current, account_id: undefined }));
                }}
              />
            </Box>
            <Box className="records-field">
              <TextField
                select
                className="category-search-select"
                value={categoryId}
                label={recordFieldLabel("Category", <CategoryOutlinedIcon fontSize="small" aria-hidden="true" />)}
                slotProps={{
                  inputLabel: { shrink: true },
                  select: {
                    open: false,
                    displayEmpty: true,
                    onOpen: () => setCategoryPickerOpen(true),
                    renderValue: () =>
                      selectedCategory ? <CategorySelectOption category={selectedCategory} indent={false} /> : "No category",
                  },
                }}
              >
                <MenuItem value="">No category</MenuItem>
                {selectedCategory ? (
                  <MenuItem value={selectedCategory.id}>
                    <CategorySelectOption category={selectedCategory} />
                  </MenuItem>
                ) : null}
              </TextField>
            </Box>
            <Box component="fieldset" className="records-field record-tags-field">
              <legend className="record-tags-label">
                <LocalOfferOutlinedIcon fontSize="small" aria-hidden="true" />
                Tags
              </legend>
              <Box className="record-tags-chips">
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    size="small"
                    label={tag.name}
                    style={{ backgroundColor: tag.color, color: "#fff" }}
                    onDelete={() => setTagIds((current) => current.filter((tagId) => tagId !== tag.id))}
                  />
                ))}
                <Chip
                  className="record-add-tag-chip"
                  size="small"
                  variant="outlined"
                  clickable
                  icon={<AddCircleOutlineIcon fontSize="small" />}
                  label="Add tag"
                  onClick={() => setTagPickerOpen(true)}
                />
              </Box>
            </Box>
            <Box className="records-field recurring-schedule-row">
              <TextField
                label={recordFieldLabel("Repeat every", <EventRepeatOutlinedIcon fontSize="small" aria-hidden="true" />)}
                name="interval"
                type="number"
                value={interval}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: 1, max: 36 } }}
                onChange={(event) => setInterval(event.target.value)}
              />
              <TextField
                select
                name="frequency"
                value={frequency}
                aria-label="Repeat period"
                slotProps={{ select: { native: true } }}
                onChange={(event) => setFrequency(event.target.value as RecurrenceFrequency)}
              >
                {(Object.keys(recurrenceFrequencyUnits) as RecurrenceFrequency[]).map((option) => (
                  <option key={option} value={option}>
                    {recurrenceFrequencyUnits[option]}(s)
                  </option>
                ))}
              </TextField>
            </Box>
            <Box className="records-field recurring-schedule-row">
              <DatePickerField
                required
                label={recordFieldLabel("Starts", <CalendarTodayOutlinedIcon fontSize="small" aria-hidden="true" />)}
                name="start_date"
                value={startDate}
                showShortcuts={false}
                onChange={setStartDate}
              />
              <DatePickerField
                label={recordFieldLabel("Ends", <CalendarTodayOutlinedIcon fontSize="small" aria-hidden="true" />)}
                name="end_date"
                value={endDate}
                showShortcuts={false}
                onChange={setEndDate}
              />
            </Box>
            <Box className="records-field">
              <TextField
                label={recordFieldLabel("Note", <NotesOutlinedIcon fontSize="small" aria-hidden="true" />)}
                name="note"
                placeholder="Family plan"
                value={note}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 } }}
                onChange={(event) => setNote(event.target.value)}
              />
            </Box>
            {status ? <p className="status error">{status}</p> : null}
            <Box className="records-modal-actions">
              <Button variant="outlined" type="button" onClick={close}>
                Cancel
              </Button>
              <Button variant="contained" type="submit" disabled={saveMutation.isPending}>
                {payment ? "Save changes" : "Add payment"}
              </Button>
            </Box>
          </form>
          {categoryPickerOpen ? (
            <CategoryPickerPanel
              group={group}
              type={type}
              categories={categories}
              selectedCategoryId={categoryId}
              onApply={setCategoryId}
              onClose={() => setCategoryPickerOpen(false)}
              onCancelAll={() => {
                setCategoryPickerOpen(false);
                close();
              }}
            />
          ) : null}
          {tagPickerOpen ? (
            <TagPickerPanel
              group={group}
              categoryId={categoryId}
              selectedTagIds={tagIds}
              onApply={setTagIds}
              onClose={() => setTagPickerOpen(false)}
              onCancelAll={() => {
                setTagPickerOpen(false);
                close();
              }}
            />
          ) : null}
        </>
      )}
    </SidePanel>
  );
}
